import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { Loader2, MapPin, Crosshair } from 'lucide-react'
import { useMdmLocations, useSendMdmCommand } from '../../../hooks/useMdm'
import { formatDateTime } from '../../../utils/format'
import MdmMap from './MdmMap'
import { timeAgo, errorMessage } from './mobileUtils'

const RANGES = [
  { value: '1h', label: '1 h', ms: 3600e3 },
  { value: '6h', label: '6 h', ms: 6 * 3600e3 },
  { value: '24h', label: '24 h', ms: 24 * 3600e3 },
  { value: '7d', label: '7 dias', ms: 7 * 86400e3 },
  { value: 'custom', label: 'Período' },
]

// <input type="datetime-local"> trabalha em hora local; a API recebe UTC (Z).
const toLocalInput = (d) => {
  const off = d.getTimezoneOffset() * 60000
  return new Date(d.getTime() - off).toISOString().slice(0, 16)
}

export default function LocationTab({ deviceId, state }) {
  const [range, setRange] = useState('6h')
  const [customFrom, setCustomFrom] = useState(() => toLocalInput(new Date(Date.now() - 86400e3)))
  const [customTo, setCustomTo] = useState(() => toLocalInput(new Date()))
  // Âncora "agora" fixa por seleção para a queryKey não mudar a cada render.
  const [anchor, setAnchor] = useState(() => Date.now())
  const locate = useSendMdmCommand(deviceId)
  const [msg, setMsg] = useState(null)

  const { from, to } = useMemo(() => {
    if (range === 'custom') {
      const f = new Date(customFrom)
      const t = new Date(customTo)
      if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return { from: null, to: null }
      return { from: f.toISOString(), to: t.toISOString() }
    }
    const r = RANGES.find((x) => x.value === range)
    return { from: new Date(anchor - r.ms).toISOString(), to: new Date(anchor).toISOString() }
  }, [range, customFrom, customTo, anchor])

  const { data: points = [], isLoading, error, refetch } = useMdmLocations(deviceId, from, to)

  const trail = useMemo(
    () => [...points]
      .sort((a, b) => new Date(a.ts) - new Date(b.ts))
      .map((p) => [p.lat, p.lng]),
    [points]
  )

  const current = state?.lat != null && state?.lng != null
    ? [{ id: 'current', lat: state.lat, lng: state.lng, color: '#ef4444', radius: 10, label: `Posição atual — ${timeAgo(state.locationAt)}` }]
    : []

  const selectRange = (v) => {
    setRange(v)
    setAnchor(Date.now())
  }

  const handleLocate = async () => {
    setMsg(null)
    try {
      await locate.mutateAsync({ type: 'locate' })
      setMsg({ ok: true, text: 'Pedido de localização enviado. A posição atualiza assim que o tablet responder.' })
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e) })
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => selectRange(r.value)}
              className={clsx('px-3 py-1.5', range === r.value ? 'bg-theme-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}
            >
              {r.label}
            </button>
          ))}
        </div>
        {range === 'custom' && (
          <div className="flex items-center gap-1 text-sm">
            <input type="datetime-local" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1" />
            <span className="text-gray-400">até</span>
            <input type="datetime-local" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1" />
          </div>
        )}
        <button type="button" onClick={() => { setAnchor(Date.now()); refetch() }} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
          Atualizar
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={handleLocate}
          disabled={locate.isPending}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-theme-primary text-white rounded-lg hover:bg-theme-primary/90 disabled:opacity-50"
        >
          {locate.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
          Localizar agora
        </button>
      </div>

      {msg && <p className={clsx('text-sm', msg.ok ? 'text-green-700' : 'text-red-600')}>{msg.text}</p>}
      {error && <p className="text-sm text-red-600">{errorMessage(error, 'Erro ao carregar o histórico de posições.')}</p>}

      <div className="relative">
        <MdmMap markers={current} trail={trail} height={480} fitKey={`${from}|${to}|${trail.length}|${current.length}`} />
        {isLoading && (
          <div className="absolute top-2 right-2 z-[1000] bg-white/90 rounded px-2 py-1 text-xs text-gray-600 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> carregando trilha
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
        <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-red-500" />
          {state?.lat != null
            ? <>Atual: {state.lat.toFixed(5)}, {state.lng.toFixed(5)} ({timeAgo(state.locationAt)})</>
            : 'Sem posição atual'}
        </span>
        <span>{points.length} ponto(s) no período{points.length >= 5000 ? ' (limite atingido — reduza o período)' : ''}</span>
        {points.length > 0 && (
          <span>Último: {formatDateTime(points.reduce((a, b) => (new Date(a.ts) > new Date(b.ts) ? a : b)).ts)}</span>
        )}
      </div>
    </div>
  )
}
